import { groupBy } from "lodash";
import { useFetchInformationAssetAccesses } from "../../hooks/queries/information-asset-accesses";
import { useFetchAllSystemResources } from "../../hooks/queries/system-resources";
import { useFetchAllSystemUserGroups } from "../../hooks/queries/system-user-groups";
import {
  GroupAppliedScopesByUsersCount
} from "../../models/current-applied-scopes";
import { ResourceAccess } from "../../models/information-asset-accesses";
import { SystemResource } from "../../models/system-resources";
import { SystemUserGroup } from "../../models/system-user-group";
import { AccessDetailProps } from "./index";

export function useAccessDetailsController(props: AccessDetailProps) {
  const resourceId = props.resourceId;

  const currentSystemResourceAccesses = useFetchInformationAssetAccesses();
  const systemResources = useFetchAllSystemResources();
  const systemUserGroups = useFetchAllSystemUserGroups();

  const buildSystemResourcesUserGroupMap = (
    resourceAccesses: ResourceAccess[],
    systemResourceId: string,
    systemId: string
  ) => {
    const systemResourcesWithUserGroupIds: Array<{
      userGroupAndSystemId: string;
      systemResourceId: string;
      systemId: string;
    }> = [];
    resourceAccesses.forEach((resourceAccess) => {
      resourceAccess.groups().forEach((access) => {
        systemResourcesWithUserGroupIds.push({
          userGroupAndSystemId: `${access.principal.id},${systemId}`,
          systemId: systemId,
          systemResourceId: systemResourceId,
        });
      });
    });
    return systemResourcesWithUserGroupIds;
  };

  function mapSystemResourceToSystemResourceAndSystemId() {
    const systemResourcesMap: { [systemResourceAndSystemId: string]: SystemResource } = {};
    currentSystemResourceAccesses.currentAccesses.forEach((currentSystemResourceAccess) => {
      const systemResource = systemResources.systemResources.find(
        (systemResource) =>
          systemResource.systemResourceId ===
            currentSystemResourceAccess.systemResource.systemResourceId &&
          systemResource.system.id === currentSystemResourceAccess.systemResource.systemId
      );
      if (systemResource) {
        const systemResourceAndSystemId = `${currentSystemResourceAccess.systemResource.systemResourceId},${currentSystemResourceAccess.systemResource.systemId}`;
        systemResourcesMap[systemResourceAndSystemId] = systemResource;
      }
    });
    return systemResourcesMap;
  }

  const getTableData = () => {
    const currentSystemResourceAccessesAssociatedToResourceId =
      currentSystemResourceAccesses.currentAccesses.filter((currentSystemResourceAccess) =>
        currentSystemResourceAccess.isCurrentSystemResourceAccessAssociatedToResource(resourceId)
      );

    const systemResourcesMap: { [systemResourceAndSystemId: string]: SystemResource } =
      mapSystemResourceToSystemResourceAndSystemId();

    const resourceAccessesAssociatedToResource =
      currentSystemResourceAccessesAssociatedToResourceId.map((currentSystemResourceAccess) => ({
        systemResourceId: currentSystemResourceAccess.systemResource.systemResourceId,
        systemId: currentSystemResourceAccess.systemResource.systemId,
        resourceAccesses:
          currentSystemResourceAccess.getResourceAccessesAssociatedToResource(resourceId),
      }));

    const userGroupSystemResourcesIdMap: {
      [userGroupAndSystemId: string]: {
        userGroupAndSystemId: string;
        systemResourceId: string;
        systemId: string;
      }[];
    } = groupBy(
      resourceAccessesAssociatedToResource
        .map(({ resourceAccesses, systemResourceId, systemId }) => {
          return buildSystemResourcesUserGroupMap(resourceAccesses, systemResourceId, systemId);
        })
        .flat(),
      "userGroupAndSystemId"
    );

    const userGroupSystemResourceTypeMap: {
      [userGroupAndSystemId: string]: { [systemResourceType: string]: SystemResource[] };
    } = {};
    Object.keys(userGroupSystemResourcesIdMap).forEach((userGroupAndSystemId) => {
      userGroupSystemResourceTypeMap[userGroupAndSystemId] = groupBy(
        userGroupSystemResourcesIdMap[userGroupAndSystemId].map(
          ({ systemResourceId, systemId }) => {
            return systemResourcesMap[`${systemResourceId},${systemId}`];
          }
        ),
        "type"
      );
    });

    const tableData: Array<
      SystemUserGroup & {
        systemResources: { type: string; systemResources: SystemResource[] }[];
      } & { currentAppliedScopesWithCount: GroupAppliedScopesByUsersCount }
    > = [];
    Object.keys(userGroupSystemResourceTypeMap).forEach((userGroupAndSystemId) => {
      const [userGroupId, systemId] = userGroupAndSystemId.split(",");
      const systemUserGroup = systemUserGroups.find(
        (systemUserGroup) =>
          systemUserGroup.systemUserGroupId === userGroupId && systemId === systemUserGroup.systemId
      );
      if (systemUserGroup) {
        tableData.push({
          ...systemUserGroup,
          systemResources: Object.keys(
            userGroupSystemResourceTypeMap[`${userGroupId},${systemId}`]
          ).map((systemResourceType) => ({
            type: systemResourceType,
            systemResources:
              userGroupSystemResourceTypeMap[`${userGroupId},${systemId}`][systemResourceType],
          })),
          currentAppliedScopesWithCount: currentSystemResourceAccesses.overAllScopesForTheSystemUserGroup(systemUserGroup.systemId,systemUserGroup.systemUserGroupId,resourceId)
        });
      }
    });
    return tableData;
  };

  return {
    getTableData,
  };
}
